import { useEffect, useState } from "react";

import type { CmsParagraph } from "@/pages/shared/cmsContent";
import { localizeContent } from "@/i18n/translations";
import { getSiteLocale } from "@/i18n/locale";

export type SiteContact = {
  email: string;
  landlinePhone: string;
  mobilePhone: string;
  address: string;
  website: string;
  mapsUrl: string;
  mapsEmbedUrl: string;
};

type SiteContactApiResponse = {
  email: string;
  landline_phone: string;
  mobile_phone: string;
  address: string;
  website: string;
};

export const SITE_CONTACT_FALLBACK: SiteContact = {
  email: "info@venuex.gr",
  landlinePhone: "+30 210 4321374",
  mobilePhone: "+30 697 214 5680",
  address: "22 Kefallinias Street, Keratsini 187 57, Greece",
  website: "https://venuex.gr",
  // The VenueX Google Business Profile (place id 0x14a1bb3b4adf1673:0x490585e77f1ff731).
  // The embed opens the profile itself (cid), so it shows the VenueX listing with its rating.
  // Searching the street address instead showed a bare address card, not the business.
  mapsUrl:
    "https://www.google.com/maps/place/VenueX/@37.9672698,23.6194629,957m/data=!3m2!1e3!4b1!4m6!3m5!1s0x14a1bb3b4adf1673:0x490585e77f1ff731!8m2!3d37.9672698!4d23.6220378!16s%2Fg%2F11zwp3t614",
  mapsEmbedUrl: "https://maps.google.com/maps?cid=5261758968969033521&output=embed",
};

function mapSiteContact(data: SiteContactApiResponse): SiteContact {
  return localizeContent({
    email: data.email || SITE_CONTACT_FALLBACK.email,
    landlinePhone: data.landline_phone || SITE_CONTACT_FALLBACK.landlinePhone,
    mobilePhone: data.mobile_phone || SITE_CONTACT_FALLBACK.mobilePhone,
    address: data.address || SITE_CONTACT_FALLBACK.address,
    website: data.website || SITE_CONTACT_FALLBACK.website,
    mapsUrl: SITE_CONTACT_FALLBACK.mapsUrl,
    mapsEmbedUrl: SITE_CONTACT_FALLBACK.mapsEmbedUrl,
  });
}

export function useSiteContact() {
  const [contact, setContact] = useState<SiteContact>(localizeContent(SITE_CONTACT_FALLBACK));

  useEffect(() => {
    const controller = new AbortController();

    fetch("/api/site-contact/", {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load VenueX contact details");
        }
        return response.json() as Promise<SiteContactApiResponse>;
      })
      .then((data) => setContact(mapSiteContact(data)))
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setContact(localizeContent(SITE_CONTACT_FALLBACK));
        }
      });

    return () => controller.abort();
  }, []);

  return contact;
}

export function phoneHref(phone: string) {
  return `tel:${phone.replace(/[^+\d]/g, "")}`;
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function buildContactParagraphs(contact: SiteContact): CmsParagraph[] {
  const isGreek = getSiteLocale() === "el";
  const email = escapeHtml(contact.email);
  const landline = escapeHtml(contact.landlinePhone);
  const mobile = escapeHtml(contact.mobilePhone);
  const address = escapeHtml(contact.address);

  return [
    {
      html: `<p>Email: <a href="mailto:${email}">${email}</a></p>`,
      text: `Email: ${contact.email}`,
    },
    {
      html: `<p>${isGreek ? "Σταθερό" : "Landline"}: <a href="${phoneHref(contact.landlinePhone)}">${landline}</a></p>`,
      text: `${isGreek ? "Σταθερό" : "Landline"}: ${contact.landlinePhone}`,
    },
    {
      html: `<p>${isGreek ? "Κινητό" : "Mobile"}: <a href="${phoneHref(contact.mobilePhone)}">${mobile}</a></p>`,
      text: `${isGreek ? "Κινητό" : "Mobile"}: ${contact.mobilePhone}`,
    },
    {
      html: isGreek
        ? `<p>Γραφείο: ${address}. Οι συναντήσεις πραγματοποιούνται κατόπιν ραντεβού, ώστε κάθε brief να ξεκινά με την προσοχή και την ακρίβεια που απαιτεί.</p>`
        : `<p>Office: ${address}. Meetings are arranged by appointment so each brief can begin with the right level of care and precision.</p>`,
      text: isGreek
        ? `Γραφείο: ${contact.address}. Οι συναντήσεις πραγματοποιούνται κατόπιν ραντεβού, ώστε κάθε brief να ξεκινά με την προσοχή και την ακρίβεια που απαιτεί.`
        : `Office: ${contact.address}. Meetings are arranged by appointment so each brief can begin with the right level of care and precision.`,
    },
  ];
}
